import React, { useState } from 'react';
import FormSection from './FormSection';

class TeamSection extends FormSection {
  constructor(props) {
    super(props);
    this.state = {
      coFounders: [{ name: '', role: '', linkedIn: '' }],
      additionalTeamMembers: [{ role: '' }]
    };
  }

  validate() {
    const errors = [];
    const { watch } = this.props;
    
    const capacity = watch('capacity');
    const previousPartners = watch('previousPartners');
    const equitySplit = watch('equitySplit');

    if (!capacity) errors.push('Capacity is required');
    if (!previousPartners) errors.push('Previous business partners question is required');
    if (!equitySplit) errors.push('Equity split description is required');

    // Validate co-founders
    const coFounders = this.state.coFounders;
    coFounders.forEach((founder, index) => {
      if (!founder.name) errors.push(`Co-founder ${index + 1} name is required`);
      if (!founder.role) errors.push(`Co-founder ${index + 1} role is required`);
      if (!founder.linkedIn) errors.push(`Co-founder ${index + 1} LinkedIn is required`);
    });

    return errors;
  }

  addCoFounder = () => {
    this.setState(prevState => ({
      coFounders: [...prevState.coFounders, { name: '', role: '', linkedIn: '' }]
    }));
  };

  removeCoFounder = (index) => {
    if (this.state.coFounders.length > 1) {
      this.setState(prevState => ({
        coFounders: prevState.coFounders.filter((_, i) => i !== index)
      }));
    }
  };

  updateCoFounder = (index, field, value) => {
    this.setState(prevState => ({
      coFounders: prevState.coFounders.map((founder, i) => 
        i === index ? { ...founder, [field]: value } : founder
      )
    }));
  };

  addTeamMember = () => {
    this.setState(prevState => ({
      additionalTeamMembers: [...prevState.additionalTeamMembers, { role: '' }]
    }));
  };

  removeTeamMember = (index) => {
    if (this.state.additionalTeamMembers.length > 1) {
      this.setState(prevState => ({
        additionalTeamMembers: prevState.additionalTeamMembers.filter((_, i) => i !== index)
      }));
    }
  };

  updateTeamMember = (index, field, value) => {
    this.setState(prevState => ({
      additionalTeamMembers: prevState.additionalTeamMembers.map((member, i) => 
        i === index ? { ...member, [field]: value } : member
      )
    }));
  };

  render() {
    const { watch } = this.props;
    const capacity = watch('capacity');
    const previousPartners = watch('previousPartners');

    return (
      <div className="border border-gray-200 rounded-lg p-6 mb-6">
        {this.renderSectionHeader('Part 2: Team')}
        
        <div className="space-y-6">
          {/* Co-Founders */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Co-Founders</h3>
            <p className="text-sm text-gray-600 mb-4">
              Please list all co-founders, their roles, and a link to their LinkedIn profiles.
            </p>
            
            <div className="space-y-4">
              {this.state.coFounders.map((founder, index) => (
                <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border border-gray-200 rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={founder.name}
                      onChange={(e) => this.updateCoFounder(index, 'name', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Full Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={founder.role}
                      onChange={(e) => this.updateCoFounder(index, 'role', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="e.g., CEO, CTO, COO"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      LinkedIn Profile <span className="text-red-500">*</span>
                    </label>
                    <div className="flex">
                      <input
                        type="url"
                        value={founder.linkedIn}
                        onChange={(e) => this.updateCoFounder(index, 'linkedIn', e.target.value)}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        placeholder="https://linkedin.com/in/..."
                      />
                      {this.state.coFounders.length > 1 && (
                        <button
                          type="button"
                          onClick={() => this.removeCoFounder(index)}
                          className="ml-2 px-3 py-2 text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              <button
                type="button"
                onClick={this.addCoFounder}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                + Add Co-Founder
              </button>
            </div>
          </div>

          {/* Capacity */}
          <div>
            {this.renderField({
              name: 'capacity',
              label: 'Capacity: What are you and your co-founders current capacity?',
              type: 'radio',
              required: true,
              options: [
                { value: 'full-time', label: 'Full-time' },
                { value: 'part-time', label: 'Part-time (explain)' }
              ]
            })}
            
            {/* Conditional field for part-time explanation */}
            {this.renderConditionalField(
              { field: 'capacity', equals: 'part-time' },
              {
                name: 'partTimeExplanation',
                label: 'Please explain your part-time capacity',
                type: 'textarea',
                required: true,
                placeholder: 'Explain your current commitments and how much time you can dedicate to the business'
              }
            )}
          </div>

          {/* Previous working together */}
          {this.renderField({
            name: 'workedTogether',
            label: 'Have any team members worked together before? If so, describe how.',
            type: 'textarea',
            required: false,
            placeholder: 'Describe previous working relationships, or leave blank if team members have not worked together before'
          })}

          {/* Previous Business Partners */}
          <div>
            {this.renderField({
              name: 'previousPartners',
              label: 'Previous Business Partners: Have you had any co-founders or business partners who are no longer with the company?',
              type: 'radio',
              required: true,
              options: [
                { value: 'yes', label: 'Yes' },
                { value: 'no', label: 'No' }
              ]
            })}
            
            {/* Conditional field for departure explanation */}
            {this.renderConditionalField(
              { field: 'previousPartners', equals: 'yes' },
              {
                name: 'departureExplanation',
                label: 'Please briefly explain the circumstances of their departure and any severance or equity agreements that were made.',
                type: 'textarea',
                required: true,
                placeholder: 'Explain the circumstances and any agreements made'
              }
            )}
          </div>

          {/* Equity Split */}
          {this.renderField({
            name: 'equitySplit',
            label: 'Equity Split + Roles: Please briefly describe how you decided on the equity split between the founders and on the role each one would have.',
            type: 'textarea',
            required: true,
            placeholder: 'Describe the reasoning behind your equity split and role assignments'
          })}

          {/* Additional Team Members */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Additional Team Members</h3>
            <p className="text-sm text-gray-600 mb-4">
              List any additional team members who are not founders.
            </p>
            
            <div className="space-y-4">
              {this.state.additionalTeamMembers.map((member, index) => (
                <div key={index} className="flex items-center space-x-4">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={member.role}
                      onChange={(e) => this.updateTeamMember(index, 'role', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Role (e.g., Developer, Designer, Advisor)"
                    />
                  </div>
                  {this.state.additionalTeamMembers.length > 1 && (
                    <button
                      type="button"
                      onClick={() => this.removeTeamMember(index)}
                      className="px-3 py-2 text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              
              <button
                type="button"
                onClick={this.addTeamMember}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                + Add Team Member
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default TeamSection;